'use client';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Play, Pause, Volume2, VolumeX, Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Slider } from '@/shared/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { downloadFile } from '@/contexts/media/utils/download';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';

interface AudioPlayerProps {
  audioUrl: string;
  title?: string;
  className?: string;
  downloadFilename?: string;
  duration?: number; // Optional pre-calculated duration in seconds
  hideDownload?: boolean;
  /** Compact vertical layout for narrow containers (sidebar) */
  compact?: boolean;
  /** Start playback as soon as the source is set — for players that open FROM a
   *  play gesture (the narration chip), so the listener doesn't press play twice. */
  autoPlay?: boolean;
}

export function AudioPlayer({ audioUrl, title, className = '', downloadFilename, duration: propDuration, hideDownload, compact, autoPlay }: AudioPlayerProps) {
  const t = useTranslations('stories.audio.player');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [detectedDuration, setDetectedDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('playQueue.playbackSpeed');
      return saved ? parseFloat(saved) : 1.0;
    }
    return 1.0;
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);

  // Use prop duration if available, otherwise use detected duration
  const duration = propDuration || detectedDuration;

  // Create persistent Audio() instance ONCE — survives prop changes.
  // AudioContext is created lazily on first play() to avoid browser limits
  // (Chrome warns at 6 concurrent AudioContexts, may break above that).
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
      }
      audioCtxRef.current = null;
      filterRef.current = null;
      audioRef.current = null;
    };
  }, []); // Persistent audio element — created once, never recreated

  /** Connect Web Audio API (lowpass filter for speed artifacts) on first user play.
   *  Deferred to avoid creating 25+ AudioContexts on page load. */
  const ensureWebAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audioCtxRef.current) return;
    try {
      const ctx = new AudioContext();
      const source = ctx.createMediaElementSource(audio);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 20000;
      filter.Q.value = 0.7;
      source.connect(filter);
      filter.connect(ctx.destination);
      audioCtxRef.current = ctx;
      filterRef.current = filter;
    } catch {
      // Fallback: AudioContext not available — play without filter
    }
  }, []);

  // When audioUrl changes, update the persistent audio element's source.
  // No prevUrl guard needed — React's [audioUrl] dep already prevents re-runs.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Reset playback state for new source
    setIsPlaying(false);
    setCurrentTime(0);
    setDetectedDuration(0);

    // Guard empty/unready refs: setting `audio.src = ''` makes the element report
    // "no supported sources" and a play attempt throws NotSupportedError. Clear the
    // source instead until a real url arrives (block still generating / not assembled).
    const src = mediaSrc(audioUrl);
    if (!src) {
      audio.removeAttribute('src');
      audio.load();
      return;
    }
    audio.src = src;
    audio.load();
  }, [audioUrl]);

  // Attach event listeners to the persistent audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (!propDuration && audio.duration && !isNaN(audio.duration)) {
        setDetectedDuration(audio.duration);
      }
    };
    const handleEnded = () => setIsPlaying(false);
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
    };
  }, [propDuration]); // Only re-bind if propDuration changes (audio element is stable)

  // Apply playback speed and adjust low-pass filter to reduce artifacts
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackSpeed;
      // Cross-browser preservesPitch
      (audio as any).preservesPitch = true;
      (audio as any).mozPreservesPitch = true;
      (audio as any).webkitPreservesPitch = true;
    }
    // Adjust low-pass filter: at slower speeds browser time-stretching introduces
    // high-frequency artifacts. Gently roll off above Nyquist-scaled cutoff.
    if (filterRef.current) {
      // At 1x → 20kHz (transparent), at 0.5x → 10kHz (removes HF artifacts)
      const cutoff = Math.min(20000, 20000 * playbackSpeed);
      filterRef.current.frequency.value = cutoff;
    }
  }, [playbackSpeed, audioUrl]);

  // Sync playback speed across components:
  // - storage event: cross-tab sync (fires only in OTHER tabs)
  // - custom event: same-page sync (fires in THIS tab for other AudioPlayer instances)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'playQueue.playbackSpeed' && e.newValue) {
        setPlaybackSpeed(parseFloat(e.newValue));
      }
    };
    const handleSpeedSync = (e: Event) => {
      const speed = (e as CustomEvent<number>).detail;
      setPlaybackSpeed(speed);
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('playbackSpeedChange', handleSpeedSync);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('playbackSpeedChange', handleSpeedSync);
    };
  }, []);

  const handleSpeedChange = useCallback((value: string) => {
    const speed = parseFloat(value);
    setPlaybackSpeed(speed);
    localStorage.setItem('playQueue.playbackSpeed', speed.toString());
    // Notify other AudioPlayer instances on the same page
    window.dispatchEvent(new CustomEvent('playbackSpeedChange', { detail: speed }));
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    // Use audio.paused as source of truth instead of React state
    // to avoid stale closure issues on mobile (rapid taps)
    if (!audio.paused) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      // Create AudioContext lazily on first user play gesture
      // (avoids exceeding browser limit of ~6 concurrent contexts)
      ensureWebAudio();

      // Resume AudioContext on user gesture (browsers require it)
      if (audioCtxRef.current?.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Audio play failed:', err);
      setIsPlaying(false);
    }
  }, [ensureWebAudio]);

  // Auto-start once the source is in place (runs after the src effect above).
  useEffect(() => {
    if (!autoPlay || !mediaSrc(audioUrl)) return;
    void togglePlay();
  }, [autoPlay, audioUrl, togglePlay]);

  const handleSeek = useCallback((value: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value[0];
    setCurrentTime(value[0]);
  }, []);

  // Keyboard seek: ←/→ = ±10s (only while THIS player is playing, so several
  // players on one page don't all jump; typing in inputs is never hijacked).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const target = e.target as HTMLElement | null;
      if (target && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.isContentEditable
        || target.getAttribute('role') === 'slider'
      )) return;
      const audio = audioRef.current;
      if (!audio || audio.paused) return;
      e.preventDefault();
      const delta = e.key === 'ArrowRight' ? 10 : -10;
      const max = audio.duration && !isNaN(audio.duration) ? audio.duration : Infinity;
      const next = Math.min(Math.max(audio.currentTime + delta, 0), max);
      audio.currentTime = next;
      setCurrentTime(next);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleVolumeChange = useCallback((value: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVolume = value[0];
    audio.volume = newVolume;
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const formatTime = useMemo(() => {
    return (time: number) => {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };
  }, []);

  const handleDownload = useCallback(async () => {
    const filename = downloadFilename || 'audio.mp3';
    try {
      // Uses the shared helper: server-action mints a SAS with
      // Content-Disposition: attachment, then Telegram.WebApp.openLink (or a
      // plain anchor in a regular browser) triggers the download. The old
      // fetch→blob path failed CORS inside Telegram's in-app WebView.
      await downloadFile(audioUrl, filename);
    } catch (error) {
      console.error('Failed to download audio:', error);
    }
  }, [audioUrl, downloadFilename]);

  if (compact) {
    // Compact 2-row layout for narrow containers (sidebar)
    // Row 1: seek bar with timestamps
    // Row 2: play + speed + volume + download
    return (
      <div data-testid="audio-player" className={`flex flex-col gap-1.5 p-2 bg-muted rounded-lg ${className}`}>

        {/* Row 1: progress bar */}
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[10px] text-muted-foreground w-8 shrink-0 text-right tabular-nums">
            {formatTime(currentTime)}
          </span>
          <Slider
            data-testid="audio-seek-slider"
            value={[currentTime]}
            max={duration || 100}
            step={0.1}
            onValueChange={handleSeek}
            className="flex-1 min-w-[40px]"
          />
          <span className="text-[10px] text-muted-foreground w-8 shrink-0 tabular-nums">
            {formatTime(duration)}
          </span>
        </div>

        {/* Row 2: controls */}
        <div className="flex items-center gap-1.5">
          <Button
            data-testid="audio-play-button"
            size="sm"
            variant="outline"
            onClick={togglePlay}
            className="h-7 w-7 p-0 shrink-0"
          >
            {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </Button>

          <Select value={String(playbackSpeed)} onValueChange={handleSpeedChange}>
            <SelectTrigger
              data-testid="audio-speed-select"
              className="h-7 w-[54px] shrink-0 text-[10px] font-medium tabular-nums px-1.5"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1x</SelectItem>
              <SelectItem value="0.9">0.9x</SelectItem>
              <SelectItem value="0.8">0.8x</SelectItem>
              <SelectItem value="0.75">0.75x</SelectItem>
              <SelectItem value="0.5">0.5x</SelectItem>
            </SelectContent>
          </Select>

          <Button
            data-testid="audio-mute-button"
            size="sm"
            variant="outline"
            onClick={toggleMute}
            className="h-7 w-7 p-0 shrink-0"
          >
            {isMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </Button>

          {!hideDownload && (
            <Button
              data-testid="audio-download-button"
              size="sm"
              variant="outline"
              onClick={handleDownload}
              className="h-7 w-7 p-0 shrink-0 ml-auto"
              title={t('download')}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audio-player" className={`flex flex-col gap-1.5 sm:gap-0 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg ${className}`}>

      {/* Row 1 on mobile: play + progress bar */}
      <div className="flex items-center gap-2 sm:contents">
        {/* Play/Pause Button */}
        <Button
          data-testid="audio-play-button"
          size="sm"
          variant="outline"
          onClick={togglePlay}
          className="h-8 w-8 sm:h-9 sm:w-9 p-0 shrink-0"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </Button>

        {/* Progress Bar */}
        <div className="flex-1 flex items-center gap-1 sm:gap-2 min-w-0">
          <span className="text-xs text-muted-foreground w-10 sm:w-14 shrink-0 text-right tabular-nums">
            {formatTime(currentTime)}
          </span>
          <Slider
            data-testid="audio-seek-slider"
            value={[currentTime]}
            max={duration || 100}
            step={0.1}
            onValueChange={handleSeek}
            className="flex-1 min-w-[60px]"
          />
          <span className="text-xs text-muted-foreground w-10 sm:w-14 shrink-0 font-medium tabular-nums">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Row 2 on mobile: speed + download */}
      <div className="flex items-center gap-2 sm:contents">
        {/* Speed Select */}
        <Select
          value={String(playbackSpeed)}
          onValueChange={handleSpeedChange}
        >
          <SelectTrigger
            data-testid="audio-speed-select"
            className="h-7 w-[62px] sm:h-9 sm:w-[76px] shrink-0 text-xs font-medium tabular-nums px-1.5 sm:px-2"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1x</SelectItem>
            <SelectItem value="0.9">0.9x</SelectItem>
            <SelectItem value="0.8">0.8x</SelectItem>
            <SelectItem value="0.75">0.75x</SelectItem>
            <SelectItem value="0.5">0.5x</SelectItem>
          </SelectContent>
        </Select>

        {/* Volume Control - Hidden on mobile */}
        <div className="hidden sm:flex items-center gap-2">
          <Button
            data-testid="audio-mute-button"
            size="sm"
            variant="outline"
            onClick={toggleMute}
            className="h-8 w-8 p-0"
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
          <Slider
            data-testid="audio-volume-slider"
            value={[isMuted ? 0 : volume]}
            max={1}
            step={0.01}
            onValueChange={handleVolumeChange}
            className="w-20"
          />
        </div>

        {/* Download Button */}
        {!hideDownload && (
          <Button
            data-testid="audio-download-button"
            size="sm"
            variant="outline"
            onClick={handleDownload}
            className="h-7 w-7 sm:h-9 sm:w-9 p-0 shrink-0 ml-auto sm:ml-0"
            title={t('download')}
          >
            <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
